import {
  faArrowDownLeft,
  faArrowUpRight,
  faCartShopping,
  faGavel,
  faGear,
  faList,
  faSignOut,
} from '@fortawesome/pro-solid-svg-icons';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '__generated__/globalTypes';
import { Dropdown } from 'atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from 'atoms/icons';
import { StarBall } from 'atoms/icons/StarBall';
import { SBHorizontal, Vertical } from 'atoms/layout/flex';
import { CloseButton } from 'components/dialog/CloseButton';
import { MoreNavItems } from 'components/navigation/NewAppNavigation/NewNavItems/MoreNavItems';
import { Footer } from 'components/navigation/NewAppNavigation/NewNavItems/MoreNavItems/Footer';
import { SportSelector } from 'components/navigation/NewAppNavigation/SportSelector';
import { MarketCredits } from 'components/navigation/NewAppNavigation/UserCurrencies/MarketCredits';
import { MoneyBalances } from 'components/navigation/NewAppNavigation/UserCurrencies/MoneyBalances';
import { NewNavItem, Separator } from 'components/navigation/NewNavItem';
import {
  ANY_SPORT_MY_CLUB,
  MY_SORARE_AUCTIONS,
  MY_SORARE_INSTANT_BUYS,
  MY_SORARE_OFFERS_RECEIVED,
  MY_SORARE_OFFERS_SENT,
  MY_SORARE_SALES,
  SETTINGS,
} from 'constants/__generated__/routes';
import { MY_SORARE_HOME } from 'constants/routes';
import { useWalletContext } from 'contexts/wallet';
import { useWalletDrawerContext } from 'contexts/walletDrawer';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useInviteLink } from 'hooks/useInviteLink';
import { glossary, navLabels } from 'lib/glossary';
import { generateSportPath } from 'lib/routing/generateSportPath';
import { laptopAndAbove } from 'style/mediaQuery';
import { regularMaterial } from 'style/utils';

import { MyClubNavItem } from './MyClubNavItem';
import { ProfileButtonContent } from './ProfileButtonContent';
import { ReferalProgramBlock } from './ReferralProgramBlock';

const DropdownContent = styled(Vertical).attrs({ gap: 2 })`
  top: 0;
  padding: var(--double-unit);
  ${regularMaterial}

  @media ${laptopAndAbove} {
    width: 375px;
  }
`;

type Props = { sport: Sport };
export const UserNav = ({ sport }: Props) => {
  const inviteLink = useInviteLink();
  const { showDrawer } = useWalletDrawerContext();
  const { formatMessage } = useIntl();
  const isLaptopAndAbove = useIsLaptop();
  const walletContext = useWalletContext();

  return (
    <Dropdown
      transparent
      forceDesktopBehavior={isLaptopAndAbove}
      label={({ onClick, open }) => (
        <button type="button" onClick={onClick}>
          <ProfileButtonContent sport={sport} active={open} />
        </button>
      )}
      side={isLaptopAndAbove ? 'bottom' : 'left'}
      gap={16}
    >
      {({ closeDropdown }) => (
        <DropdownContent>
          {!isLaptopAndAbove && (
            <SBHorizontal>
              <SportSelector displaySelectedSportName />
              <CloseButton onClose={closeDropdown} />
            </SBHorizontal>
          )}
          <MyClubNavItem
            to={generateSportPath(ANY_SPORT_MY_CLUB, { sport })}
            onClick={closeDropdown}
          />
          {!isLaptopAndAbove && (
            <>
              <button onClick={showDrawer} type="button">
                <MoneyBalances />
              </button>
              <button onClick={showDrawer} type="button">
                <MarketCredits sport={sport} />
              </button>
              <ReferalProgramBlock referralUrl={inviteLink} />
            </>
          )}
          <Vertical>
            <NewNavItem
              useClassicLink
              to={MY_SORARE_HOME}
              icon={() => <StarBall width={16} height={16} />}
              label={formatMessage(navLabels.mySorare)}
              onClick={closeDropdown}
            />
            <NewNavItem
              useClassicLink
              to={MY_SORARE_AUCTIONS}
              icon={() => <FontAwesomeIcon icon={faGavel} />}
              label={formatMessage(navLabels.myAuctions)}
              onClick={closeDropdown}
            />
            <NewNavItem
              useClassicLink
              to={MY_SORARE_INSTANT_BUYS}
              icon={() => <FontAwesomeIcon icon={faCartShopping} />}
              label={formatMessage(navLabels.myInstantBuys)}
              onClick={closeDropdown}
            />
            <NewNavItem
              useClassicLink
              to={MY_SORARE_SALES}
              icon={() => <FontAwesomeIcon icon={faList} />}
              label={formatMessage(navLabels.myListings)}
              onClick={closeDropdown}
            />
            <NewNavItem
              useClassicLink
              to={MY_SORARE_OFFERS_RECEIVED}
              icon={() => <FontAwesomeIcon icon={faArrowDownLeft} />}
              label={formatMessage(navLabels.myOffersReceived)}
              onClick={closeDropdown}
            />
            <NewNavItem
              useClassicLink
              to={MY_SORARE_OFFERS_SENT}
              icon={() => <FontAwesomeIcon icon={faArrowUpRight} />}
              label={formatMessage(navLabels.myOffersSent)}
              onClick={closeDropdown}
            />
            <Separator />
            <NewNavItem
              icon={() => <FontAwesomeIcon icon={faGear} />}
              label={formatMessage(navLabels.settings)}
              to={SETTINGS}
            />
            {!isLaptopAndAbove && <MoreNavItems noFooter />}
            <Separator />
            <NewNavItem
              useClassicLink
              icon={() => <FontAwesomeIcon icon={faSignOut} />}
              label={formatMessage(glossary.logOut)}
              onClick={() => {
                walletContext.logOut();
                closeDropdown();
              }}
            />
            {!isLaptopAndAbove && (
              <>
                <Separator />
                <Footer />
              </>
            )}
          </Vertical>
        </DropdownContent>
      )}
    </Dropdown>
  );
};
